import {LeanDocument, Types} from 'mongoose';
import {TaxonomyTypes} from '@/enums/taxonomy-manager/TaxonomyTypes.enum';
import {TaxonomyManagerApi} from '@/classes/api/taxonomy-manager.api.class';
import {SubstitutionManagerApi} from '@/classes/api/substitution-manager.api.class';
import {TaxonomyEntryDocument} from '@/documentTypes/taxonomy-manager/TaxonomyEntry.document';
import {retrieveObjectId} from '@/helpers/isObjectId.helper';

/**
 * Ausfallgrund (Etappe 2 B4): taxonomy `courseCancellationReason` + optional free text, stored on the
 * SubstitutionInquiry (root fields `cancellationReason`, `cancellationReasonText`). Never part of mails.
 * Older backends reject the unknown taxonomy type / ignore the fields: everything here degrades to "no reason".
 */

export const CANCELLATION_REASON_TAXONOMY = TaxonomyTypes.COURSE_CANCELLATION_REASON;
export const REASON_TEXT_MAX_LENGTH = 500;

export type ReasonEntry = Pick<LeanDocument<TaxonomyEntryDocument>, '_id' | 'name'>
    & Partial<Pick<LeanDocument<TaxonomyEntryDocument>, 'order' | 'isArchived' | 'description' | 'color' | 'icon'>>;

/** Fields of an inquiry needed for the reason (populated or not) */
export interface InquiryReasonFields {
    cancellationReason?: unknown;
    cancellationReasonText?: string;
}

function idString(value: unknown): string {
    const id = retrieveObjectId(value as any);
    return id ? id.toString() : '';
}

/** Active entries ordered by `order`, then name (same as the other taxonomy pickers) */
export function sortReasonEntries<T extends ReasonEntry>(entries: T[]): T[] {
    const orderOf = (entry: T) => (typeof entry.order === 'number' ? entry.order : Number.MAX_SAFE_INTEGER);
    return entries.filter((entry) => !entry.isArchived)
        .sort((a, b) => orderOf(a) - orderOf(b) || (a.name || '').localeCompare(b.name || '', 'de'));
}

let reasonCache: Promise<ReasonEntry[]> | undefined;

/**
 * Reason entries (cached for the session; `refresh` after creating one). An empty list means: no reasons
 * configured or backend without the taxonomy type (feature not available).
 */
export function loadReasonEntries(refresh = false): Promise<ReasonEntry[]> {
    if (!reasonCache || refresh) {
        reasonCache = TaxonomyManagerApi.find({
            filter: {taxonomy: [CANCELLATION_REASON_TAXONOMY], isArchived: false},
            skipPagination: true,
        } as any)
            .then((resp) => sortReasonEntries((resp.taxonomyEntries || []) as ReasonEntry[]))
            .catch(() => {
                reasonCache = undefined;
                return [];
            });
    }
    return reasonCache;
}

/** Name of the reason entry: populated document or id looked up in `entries` */
export function reasonName(reason: unknown, entries: ReasonEntry[]): string {
    if (reason && typeof reason === 'object' && typeof (reason as { name?: unknown }).name === 'string') {
        return (reason as { name: string }).name;
    }
    const id = idString(reason);
    return id ? entries.find((entry) => idString(entry._id) === id)?.name || '' : '';
}

/** "Krankheit – Lehrkraft liegt im Krankenhaus" / "Krankheit" / free text / '' */
export function reasonLabel(inquiry: InquiryReasonFields | undefined, entries: ReasonEntry[]): string {
    if (!inquiry) {
        return '';
    }
    const name = reasonName(inquiry.cancellationReason, entries);
    const text = (inquiry.cancellationReasonText || '').trim();
    return [name, text].filter(Boolean).join(' – ');
}

/** "Fällt aus: Krankheit" (or "Fällt aus" without reason) */
export function cancellationHeadline(label: string): string {
    return label ? `Fällt aus: ${label}` : 'Fällt aus';
}

/** Request fields for create/cancel; empty values are left out so older backends see exactly the old body */
export function reasonRequestFields(reasonId: Types.ObjectId | string | null | undefined, text: string | null | undefined)
    : { cancellationReason?: Types.ObjectId; cancellationReasonText?: string } {
    const result: { cancellationReason?: Types.ObjectId; cancellationReasonText?: string } = {};
    const id = retrieveObjectId(reasonId as any);
    if (id) {
        result.cancellationReason = id;
    }
    const trimmed = (text || '').trim().slice(0, REASON_TEXT_MAX_LENGTH);
    if (trimmed) {
        result.cancellationReasonText = trimmed;
    }
    return result;
}

export const NOTIFICATION_REQUIRED_TEXT = 'Absagen ohne Information der Eltern bzw. Schüler:innen nur mit Berechtigung „Kurstermine absagen, ohne Eltern/Schüler:innen zu informieren“. Bitte E-Mail oder WhatsApp wählen.';

/**
 * Whether the cancellation may be sent: students/parents must be informed (mail and/or WhatsApp) unless the user has
 * the custom key cancelCourseWithoutNotification (backend answers 403 otherwise).
 */
export function canSendCancellation(notifyStudents: boolean, mayCancelWithoutNotification: boolean): boolean {
    return notifyStudents || mayCancelWithoutNotification;
}

/**
 * Reason labels of several inquiries with one request (plus the cached taxonomy list): inquiry id -> label.
 * Failures (older backends) give an empty map.
 */
export async function loadInquiryReasonLabels(inquiryIds: Array<Types.ObjectId | string>): Promise<Record<string, string>> {
    const ids = [...new Map(inquiryIds.map((id) => [idString(id), retrieveObjectId(id as any)])).values()]
        .filter((id): id is Types.ObjectId => !!id);
    if (!ids.length) {
        return {};
    }
    try {
        const [resp, entries] = await Promise.all([
            SubstitutionManagerApi.find({
                filter: {_id: ids},
                fields: ['cancellationReason', 'cancellationReasonText'],
                skipPagination: true,
            } as any),
            loadReasonEntries(),
        ]);
        const result: Record<string, string> = {};
        for (const inquiry of resp.substitutionInquiries || []) {
            const label = reasonLabel(inquiry as InquiryReasonFields, entries);
            if (label) {
                result[idString(inquiry._id)] = label;
            }
        }
        return result;
    } catch (e) {
        return {};
    }
}
